import { PhSparkle } from 'ember-phosphor-icons';
import Component from '@glimmer/component';
import type { DrawerContext } from '../types.ts';
import type TasApplicationModel from '../../../../models/tas-application.ts';
import type AiRecommendationModel from '../../../../models/ai-recommendation.ts';
import ApplicationAnalysisView from './ai-analysis/application-analysis-view.gts';

/**
 * AI Analysis drawer content component
 * Displays AI-generated recommendations and analysis for applications
 */
export interface AiAnalysisDrawerSignature {
  Args: {
    drawer: DrawerContext;
    application: TasApplicationModel;
    recommendations?: AiRecommendationModel[];
  };
}

class AiAnalysisDrawer extends Component<AiAnalysisDrawerSignature> {
  get latestRecommendation(): AiRecommendationModel | null {
    const recommendations = this.args.recommendations;
    if (!recommendations || recommendations.length === 0) {
      return null;
    }
    // Return the most recent recommendation
    const sorted = [...recommendations].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return sorted[0] || null;
  }

  get hasRecommendations(): boolean {
    return this.latestRecommendation !== null;
  }

  <template>
    <@drawer.Header>
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-1.5">
          <PhSparkle @size="20" class="text-purple-600" />
          <h2 class="text-lg font-semibold text-gray-800">
            {{!-- {{t "tuition_assistance.application_approval.ai_analysis"}} --}}
            Ai Details
          </h2>
        </div>
      </div>
    </@drawer.Header>

    <@drawer.Body>
      <div>
        {{#if this.hasRecommendations}}
          {{#let this.latestRecommendation as |recommendation|}}
            {{#if recommendation}}
              <ApplicationAnalysisView
                @recommendation={{recommendation}}
                @application={{@application}}
              />
            {{/if}}
          {{/let}}
        {{else}}
          {{! Empty State }}
          <div class="p-6 text-center">
            <PhSparkle @size="48" class="text-gray-400 mx-auto mb-4" />
            <h3 class="text-base font-semibold mb-2 text-gray-800">No AI Analysis Available</h3>
            <p class="text-sm text-gray-600">
              AI analysis has not been generated for this application yet.
            </p>
          </div>
        {{/if}}
      </div>
    </@drawer.Body>
  </template>
}

export default AiAnalysisDrawer;
